import React, { useState, useEffect } from 'react';
import { Search, Plus, Eye, Edit, Trash2, MapPin, Building2, Home, Users, TrendingUp, ChevronRight, AlertCircle, X, Loader, Map, DollarSign, Grid3x3, CheckCircle2, XCircle, Phone, Mail, Camera } from 'lucide-react';
import { fetchApartments, createApartment, deleteApartment, updateApartment } from './api';
import CategoryManagement from './Categories';
import UnitManagement from './Units';
import ApartmentDialog from './ApartmentDialog';
import ApartmentPhotoManagement from './ApartmentPhotoManagement';
import { useAppTheme } from '../../contexts/ThemeContext';
import { PROPERTY_TYPES } from './ApartmentDialog';
import AllUnitsView from './AllUnitsView';

const PropertyManagement = () => {
  const { palette, Colors } = useAppTheme();
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState(0); // 0: list, 1: categories, 2: units, 3: photos, 4: all-units
  const [selectedApartment, setSelectedApartment] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [openApartmentDialog, setOpenApartmentDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingApartment, setEditingApartment] = useState(null);

  useEffect(() => { loadApartments(); }, []);

  const loadApartments = async () => {
    try { setLoading(true); setApartments(await fetchApartments()); setError(null); }
    catch (err) { setError('Failed to load apartments: ' + err.message); }
    finally { setLoading(false); }
  };

  const handleManageApartmentPhotos    = (id)    => { setSelectedApartment(id); setViewMode(3); };
  const handleViewApartmentDetails     = (id)    => { setSelectedApartment(id); setViewMode(1); };
  const handleViewCategoryDetails      = (aptId, catId) => { setSelectedApartment(aptId); setSelectedCategory(catId); setViewMode(2); };
  const handleBackToApartments         = ()      => { setViewMode(0); setSelectedApartment(null); setSelectedCategory(null); };
  const handleBackToCategories         = ()      => { setViewMode(1); setSelectedCategory(null); };
  const handleViewAllUnits     = () => { setViewMode(4); };
  const handleBackFromAllUnits = () => { setViewMode(1); };

  const handleSubmitApartment = async (formData) => {
    try {
      setLoading(true);
      const formatted = {
        propertyType: formData.propertyType || 'rental',   // ← ADD THIS LINE
        name: formData.name.toUpperCase(), county: formData.county, subCounty: formData.subCounty,
        ward: formData.ward || null, image: formData.image || null,
        description: formData.description?.length > 0 ? formData.description : null,
        contacts: formData.contacts && (formData.contacts.phones?.length > 0 || formData.contacts.email) ? formData.contacts : null
      };
      if (formData.lat || formData.lng || formData.placeId || formData.formattedAddress) {
        Object.assign(formatted, { lat: formData.lat || null, lng: formData.lng || null, placeId: formData.placeId || null, formattedAddress: formData.formattedAddress || null });
      }
      await createApartment(formatted); setOpenApartmentDialog(false); await loadApartments();
    } catch (err) { setError('Failed to create apartment: ' + err.message); throw err; }
    finally { setLoading(false); }
  };

  const handleSubmitEditApartment = async (formData) => {
    try {
      setLoading(true);
      const formatted = {
        propertyType: formData.propertyType || 'rental',   // ← ADD THIS LINE
        name: formData.name.toUpperCase(), county: formData.county, subCounty: formData.subCounty,
        ward: formData.ward || null, image: formData.image || null,
        description: formData.description?.length > 0 ? formData.description : null,
        contacts: formData.contacts && (formData.contacts.phones?.length > 0 || formData.contacts.email) ? formData.contacts : null
      };
      if (formData.lat || formData.lng || formData.placeId || formData.formattedAddress) {
        Object.assign(formatted, { lat: formData.lat || null, lng: formData.lng || null, placeId: formData.placeId || null, formattedAddress: formData.formattedAddress || null });
      }
      await updateApartment(editingApartment.id, formatted); setOpenEditDialog(false); setEditingApartment(null); await loadApartments();
    } catch (err) { setError('Failed to update apartment: ' + err.message); throw err; }
    finally { setLoading(false); }
  };

  const handleDeleteApartment = async (id, name) => {
    if (window.confirm(`Delete ${name}? This action cannot be undone.`)) {
      try { setLoading(true); await deleteApartment(id); await loadApartments(); }
      catch (err) { setError('Failed to delete apartment: ' + err.message); }
      finally { setLoading(false); }
    }
  };

  const getDisplayLocation = (apt) => {
    if (apt.placeData?.formatted_address) return apt.placeData.formatted_address;
    return [apt.ward, apt.subCounty, apt.county].filter(Boolean).join(', ') || 'No location specified';
  };

  const filteredApartments = apartments.filter(apt => {
    const s = searchTerm.toLowerCase();
    return apt.name.toLowerCase().includes(s) || apt.county?.toLowerCase().includes(s) || apt.subCounty?.toLowerCase().includes(s) || apt.ward?.toLowerCase().includes(s) || apt.placeData?.formatted_address?.toLowerCase().includes(s);
  });

  const totalUnits    = apartments.reduce((s, a) => s + (a.totalUnits || 0), 0);
  const totalOccupied = apartments.reduce((s, a) => s + (a.occupiedUnits || 0), 0);
  const totalVacant   = totalUnits - totalOccupied;
  const vacancyRate   = totalUnits > 0 ? Math.round((totalVacant / totalUnits) * 100) : 0;
  const currentApartment = selectedApartment ? apartments.find(a => a.id === selectedApartment) : null;

  const P   = Colors.primary.main;
  const bg  = palette.background;
  const txt = palette.text;
  const bdr = palette.border;

  const renderPropertiesView = () => (
    <>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {[
          { label: 'Total Properties', value: apartments.length, sub: null, gradient: `linear-gradient(135deg, ${Colors.primary.dark}, ${Colors.primary.main})`, icon: <Building2 className="w-8 h-8" /> },
          { label: 'Total Units', value: totalUnits, sub: `${totalOccupied} Occupied • ${totalVacant} Vacant`, gradient: `linear-gradient(135deg, ${Colors.status.success}, #34D399)`, icon: <Home className="w-8 h-8" /> },
          { label: 'Vacancy Rate', value: `${vacancyRate}%`, sub: `${totalVacant} vacant units`, gradient: `linear-gradient(135deg, ${vacancyRate > 20 ? Colors.status.error : Colors.accent.purple}, ${vacancyRate > 20 ? '#F87171' : '#C084FC'})`, icon: <TrendingUp className="w-8 h-8" /> },
        ].map(({ label, value, sub, gradient, icon }) => (
          <div key={label} className="rounded-xl p-6 text-white shadow-lg" style={{ background: gradient }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-medium mb-1 text-white/90">{label}</p>
                <h3 className="text-4xl font-bold">{value}</h3>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">{icon}</div>
            </div>
            {sub && <div className="flex items-center gap-2 text-sm text-white/80"><Users className="w-4 h-4" /><span>{sub}</span></div>}
          </div>
        ))}
      </div>

      {/* Property cards */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {filteredApartments.map(apartment => {
          const vacantUnits  = apartment.totalUnits - apartment.occupiedUnits;
          const occupancyRate = apartment.totalUnits > 0 ? Math.round(((apartment.totalUnits - vacantUnits) / apartment.totalUnits) * 100) : 0;
          const statusColor  = occupancyRate >= 80 ? Colors.status.success : occupancyRate >= 60 ? Colors.status.warning : Colors.status.error;

          return (
            <div key={apartment.id} className="rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:-translate-y-1"
              style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = ''}>

              {/* Card header */}
              <div className="p-6 text-white" style={{ background: `linear-gradient(135deg, ${Colors.primary.dark}, ${Colors.primary.main})` }}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2">{apartment.name}</h3>
                    
                    <div className="flex items-start gap-2 text-white/80 mb-3">
                      <MapPin className="w-4 h-4 flex-shrink-0 mt-1" />
                      <p className="text-sm leading-relaxed">{getDisplayLocation(apartment)}</p>
                    </div>
                    {apartment.contacts && (apartment.contacts.phones?.length > 0 || apartment.contacts.email) && (
                      <div className="flex flex-wrap items-center gap-4 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                        {apartment.contacts.phones?.length > 0 && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-white/70" />
                            <div className="flex flex-wrap items-center gap-2">
                              {apartment.contacts.phones.map((phone, i) => (
                                <React.Fragment key={i}>
                                  <a href={`tel:${phone}`} className="text-sm text-white/80 hover:text-white hover:underline transition">{phone}</a>
                                  {i < apartment.contacts.phones.length - 1 && <span className="text-white/40">•</span>}
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        )}
                        {apartment.contacts.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-white/70" />
                            <a href={`mailto:${apartment.contacts.email}`} className="text-sm text-white/80 hover:text-white hover:underline transition">{apartment.contacts.email}</a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="ml-4">
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold" style={{ backgroundColor: statusColor, color: '#fff' }}>
                      {occupancyRate}% Full
                    </span>
                  </div>
                </div>
              </div>

              {/* Occupancy bar */}
              <div className="px-6 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Home className="w-4 h-4" style={{ color: txt.secondary }} />
                    <span className="text-sm font-semibold" style={{ color: txt.secondary }}>Occupancy Status</span>
                  </div>
                  <span className="text-sm font-bold" style={{ color: txt.primary }}>{apartment.occupiedUnits} / {apartment.totalUnits}</span>
                </div>
                <div className="w-full rounded-full h-2.5 mb-1" style={{ backgroundColor: bdr }}>
                  <div className="h-2.5 rounded-full transition-all duration-500" style={{ width: `${occupancyRate}%`, backgroundColor: statusColor }}></div>
                </div>
                <div className="flex items-center justify-between text-xs" style={{ color: txt.muted }}>
                  <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" style={{ color: Colors.status.success }} /><span>{apartment.occupiedUnits} Occupied</span></div>
                  <div className="flex items-center gap-1"><XCircle className="w-3 h-3" style={{ color: Colors.status.error }} /><span>{vacantUnits} Vacant</span></div>
                </div>
              </div>

              {/* Features */}
              {apartment.description?.length > 0 && (
                <div className="px-6 pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1 rounded" style={{ backgroundColor: `${P}15` }}>
                      <CheckCircle2 className="w-3.5 h-3.5" style={{ color: P }} />
                    </div>
                    <h4 className="text-xs font-bold" style={{ color: txt.primary }}>Features & Amenities</h4>
                  </div>
                  <div className="rounded-lg p-2.5 space-y-1.5" style={{ backgroundColor: bg.tertiary }}>
                    {apartment.description.slice(0, 2).map((point, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <div className="mt-1"><div className="w-1 h-1 rounded-full" style={{ backgroundColor: P }}></div></div>
                        <p className="text-xs leading-relaxed flex-1" style={{ color: txt.secondary }}>{point}</p>
                      </div>
                    ))}
                    {apartment.description.length > 2 && (
                      <button onClick={() => handleViewApartmentDetails(apartment.id)} className="text-xs font-semibold ml-3 flex items-center gap-1" style={{ color: P }}>
                        <Plus className="w-3 h-3" />{apartment.description.length - 2} more features
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Categories chips */}
              <div className="px-6 pt-4 pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1 rounded" style={{ backgroundColor: `${Colors.accent.purple}20` }}>
                    <Grid3x3 className="w-3.5 h-3.5" style={{ color: Colors.accent.purple }} />
                  </div>
                  <h4 className="text-xs font-bold" style={{ color: txt.primary }}>Unit Categories</h4>
                  <span className="ml-auto text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: bg.tertiary, color: txt.secondary }}>
                    {apartment.categories.length} {apartment.categories.length === 1 ? 'Type' : 'Types'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {apartment.categories.map(cat => (
                    <div key={cat.id} className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 transition-all cursor-pointer"
                      style={{ border: `1px solid ${bdr}`, backgroundColor: bg.secondary }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = P; e.currentTarget.style.backgroundColor = `${P}08`; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = bdr; e.currentTarget.style.backgroundColor = bg.secondary; }}>
                      <span className="text-sm font-semibold" style={{ color: txt.primary }}>{cat.name}</span>
                      <span style={{ color: txt.muted }}>•</span>
                      <span className="text-xs font-medium" style={{ color: txt.secondary }}>{cat.totalUnits} units</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions footer */}
              <div className="px-6 py-3.5" style={{ backgroundColor: bg.tertiary, borderTop: `1px solid ${bdr}` }}>
                <div className="flex items-center justify-between">
                  <button onClick={() => handleViewApartmentDetails(apartment.id)}
                    className="flex items-center gap-2 font-semibold text-sm transition group" style={{ color: P }}>
                    <Eye className="w-4 h-4" /> View Categories
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <div className="flex items-center gap-1">
                    {[
                      [<Camera className="w-4 h-4" />, Colors.accent.purple, 'Manage Photos', () => handleManageApartmentPhotos(apartment.id)],
                      [<Edit className="w-4 h-4" />,   P,                    'Edit Property', () => { setEditingApartment(apartment); setOpenEditDialog(true); }],
                      [<Trash2 className="w-4 h-4" />, Colors.status.error,  'Delete Property', () => handleDeleteApartment(apartment.id, apartment.name)],
                    ].map(([icon, color, title, fn]) => (
                      <button key={title} onClick={fn} className="p-2 rounded-lg transition" style={{ color }} title={title}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = `${color}15`}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredApartments.length === 0 && !loading && (
        <div className="text-center py-12 rounded-xl" style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
          <Building2 className="w-16 h-16 mx-auto mb-4" style={{ color: txt.muted }} />
          <h3 className="text-lg font-semibold mb-2" style={{ color: txt.primary }}>No properties found</h3>
          <p className="mb-6" style={{ color: txt.secondary }}>{searchTerm ? 'Try adjusting your search terms' : 'Get started by adding your first property'}</p>
          {!searchTerm && (
            <button onClick={() => setOpenApartmentDialog(true)}
              className="text-white px-6 py-2.5 rounded-lg font-semibold inline-flex items-center gap-2 transition" style={{ backgroundColor: P }}>
              <Plus className="w-5 h-5" /> Add Property
            </button>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg.primary }}>
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        <div className="rounded-xl shadow-sm p-6" style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: P }}>Property Management</h1>
              <p className="mt-1" style={{ color: txt.secondary }}>Manage your properties, categories, and units</p>
            </div>
            {viewMode === 0 && (
              <button onClick={() => setOpenApartmentDialog(true)}
                className="text-white font-semibold px-6 py-2.5 rounded-lg flex items-center gap-2 shadow-sm transition"
                style={{ backgroundColor: P }}>
                <Plus className="w-5 h-5" /> Add Property
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search */}
      {viewMode === 0 && (
        <div className="px-6 pb-4">
          <div className="rounded-xl shadow-sm p-4" style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="w-5 h-5" style={{ color: txt.muted }} />
              </div>
              <input type="text" placeholder="Search properties by name, county, sub county, or address..."
                value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg focus:outline-none transition"
                style={{ backgroundColor: bg.tertiary, color: txt.primary, border: `1px solid ${bdr}` }}
                onFocus={e => { e.target.style.borderColor = P; e.target.style.boxShadow = `0 0 0 2px ${P}30`; }}
                onBlur={e => { e.target.style.borderColor = bdr; e.target.style.boxShadow = ''; }} />
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="px-6 pb-6">
          <div className="rounded-xl shadow-sm p-12 text-center" style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
            <Loader className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: P }} />
            <p style={{ color: txt.secondary }}>Loading properties...</p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="px-6 pb-6">
          <div className="rounded-xl p-4 flex items-start gap-3" style={{ backgroundColor: `${Colors.status.error}10`, border: `1px solid ${Colors.status.error}40` }}>
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: Colors.status.error }} />
            <div className="flex-1">
              <p className="font-medium" style={{ color: Colors.status.error }}>{error}</p>
              <button onClick={loadApartments} className="mt-2 text-sm font-medium underline" style={{ color: Colors.status.error }}>Try Again</button>
            </div>
            <button onClick={() => setError(null)} style={{ color: Colors.status.error }}><X className="w-5 h-5" /></button>
          </div>
        </div>
      )}

      {/* Content */}
      {!loading && (
        <div className="px-6 pb-6">
          {viewMode === 0 && renderPropertiesView()}
          {viewMode === 1 && <CategoryManagement currentApartment={currentApartment} handleBackToApartments={handleBackToApartments} handleViewCategoryDetails={handleViewCategoryDetails} onApartmentDataChange={loadApartments} onViewAllUnits={handleViewAllUnits} />}
          {viewMode === 2 && <UnitManagement currentApartment={currentApartment} selectedCategory={selectedCategory} handleBackToApartments={handleBackToApartments} handleBackToCategories={handleBackToCategories} onCategoryDataChange={loadApartments} />}
          {viewMode === 4 && (
  <AllUnitsView
    currentApartment={currentApartment}
    onBack={handleBackFromAllUnits}
  />
)}
        </div>
      )}

      {viewMode === 3 && <ApartmentPhotoManagement currentApartment={currentApartment} handleBackToApartments={handleBackToApartments} />}

      <ApartmentDialog
        open={openApartmentDialog || openEditDialog}
        onClose={() => { setOpenApartmentDialog(false); setOpenEditDialog(false); setEditingApartment(null); }}
        onSubmit={openEditDialog ? handleSubmitEditApartment : handleSubmitApartment}
        apartment={editingApartment}
        loading={loading}
      />
    </div>
  );
};

export default PropertyManagement;